// ARQUIVO ATUALIZADO
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getAuth, signOut } from 'firebase/auth';

import useData from './hooks/useData';
import { callGeminiAPI } from './services/api';

import MainLayout from './components/layout/MainLayout';
import CommandPalette from './components/layout/CommandPalette';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import FlowPage from './pages/FlowPage';
import ProjectsList from './pages/ProjectsList';
import ClientsList from './pages/ClientsList';
import QuoteGeneratorPage from './pages/QuoteGeneratorPage';
import ToolsPage from './pages/ToolsPage';
import ClientRegistrationForm from './pages/ClientRegistrationForm';
import ProjectForm from './pages/ProjectForm';
import ClientDetails from './pages/ClientDetails';
import ArchivedTasks from './pages/ArchivedTasks';
import TaskDetails from './pages/TaskDetails';
import ProjectDetails from './pages/ProjectDetails';

import WelcomeModal from './components/modals/WelcomeModal';
import NewTaskModal from './components/modals/NewTaskModal';
import SettingsModal from './components/modals/SettingsModal';
import ConfirmDeleteModal from './components/modals/ConfirmDeleteModal';
import CreativeUnblockModal from './components/modals/CreativeUnblockModal';
import UsernameSetupModal from './components/modals/UsernameSetupModal';
import AvatarSelectionModal from './components/modals/AvatarSelectionModal';

const VFlowApp = () => {
    const [view, setView] = useState('dashboard');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isPaletteOpen, setIsPaletteOpen] = useState(false);
    const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [itemToDelete, setItemToDelete] = useState({ type: null, data: null });
    const [clientToEdit, setClientToEdit] = useState(null);
    const [projectToEdit, setProjectToEdit] = useState(null);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [isCreativeModalOpen, setIsCreativeModalOpen] = useState(false);
    const [newTaskDefaultStatus, setNewTaskDefaultStatus] = useState('A Fazer');
    const [creativeSuggestion, setCreativeSuggestion] = useState('');
    const [isCreativeLoading, setIsCreativeLoading] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
    const taskDetailsContainerRef = useRef(null);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const [newTaskInitialProjectId, setNewTaskInitialProjectId] = useState(null);
    const [quoteIdToOpen, setQuoteIdToOpen] = useState(null);

    const { 
        tasks, clients, projects, quotes, currentUserProfile, isUsernameSetupRequired, 
        handleSaveTask, handleDeleteTask, 
        handleSaveClient, handleDeleteClient,
        handleSaveProject, handleDeleteProject,
        handleDeleteQuote,
        handleUsernameSave, handleAvatarUpdate, handleUpdateTask,
        showFirstTimeWelcome, handleMarkWelcomeAsSeen,
        handleDeleteComment
    } = useData();

    const handleLogout = () => signOut(getAuth());
    
    const handleOpenNewTaskModal = (status, projectId = null) => {
        const newStatus = typeof status === 'string' ? status : 'A Fazer';
        setNewTaskDefaultStatus(newStatus);
        setNewTaskInitialProjectId(projectId);
        setIsNewTaskModalOpen(true);
    };

    const openTaskDetails = (task) => setSelectedTask(task);
    const closeTaskDetails = () => setSelectedTask(null);
    const handleClientClick = (client) => { setSelectedClient(client); setView('client-details'); };
    const handleProjectClick = (project) => { setSelectedProject(project); setView('project-details'); };
    
    const handleQuoteClick = (quote) => {
        setQuoteIdToOpen(quote.id);
        setView('quotes');
    };

    // --- INÍCIO DA ALTERAÇÃO 1: Nova função para lidar com seleções da paleta ---
    const handlePaletteSelection = (command) => {
        setIsPaletteOpen(false);

        if (command.action === 'navigate') {
            setView(command.destination);
        } else if (command.action === 'newTask') {
            handleOpenNewTaskModal();
        } else if (command.action === 'open') {
            switch (command.type) {
                case 'task':
                    openTaskDetails(command.item);
                    break;
                case 'project':
                    handleProjectClick(command.item);
                    break;
                case 'client':
                    handleClientClick(command.item);
                    break;
                default:
                    break;
            }
        }
    };
    // --- FIM DA ALTERAÇÃO 1 ---

    useEffect(() => {
        if (selectedTask) { document.body.style.overflow = 'hidden'; } 
        else { document.body.style.overflow = 'auto'; }
        return () => { document.body.style.overflow = 'auto'; };
    }, [selectedTask]);

    useEffect(() => {
        if (selectedTask && taskDetailsContainerRef.current) {
            taskDetailsContainerRef.current.scrollTop = 0;
        }
    }, [selectedTask]);

    useEffect(() => {
        if (selectedTask?.id) {
            const updatedTask = tasks.find(t => t.id === selectedTask.id);
            if (!updatedTask) setSelectedTask(null);
            else if (JSON.stringify(updatedTask) !== JSON.stringify(selectedTask)) setSelectedTask(updatedTask);
        }
    }, [tasks, selectedTask]);
    
    useEffect(() => {
        if (selectedClient?.id) {
            const updatedClient = clients.find(c => c.id === selectedClient.id);
            if (updatedClient && JSON.stringify(updatedClient) !== JSON.stringify(selectedClient)) {
                setSelectedClient(updatedClient);
            }
        }
    }, [clients, selectedClient]);

    const handleSelectAvatar = async (avatarUrl) => {
        await handleAvatarUpdate(avatarUrl);
        setIsAvatarModalOpen(false);
    };

    const handleSaveNewTask = async (taskData) => {
        const fullTaskData = { 
            ...taskData, 
            jobType: '', 
            comments: [], 
            subtasks: [], 
            alterationChecklist: [],
            trackedTime: 0, 
            estimatedTime: 0 
        };
        await handleSaveTask(fullTaskData);
        setIsNewTaskModalOpen(false);
    };
    
    const handleConfirmDelete = async () => {
        if (itemToDelete.data) {
            switch (itemToDelete.type) {
                case 'client': await handleDeleteClient(itemToDelete.data.id); setView('clients'); break;
                case 'project': await handleDeleteProject(itemToDelete.data.id); setView('projects'); break;
                case 'task': await handleDeleteTask(itemToDelete.data.id); break;
                case 'quote': await handleDeleteQuote(itemToDelete.data.id); break;
                default: break;
            }
            setItemToDelete({ type: null, data: null });
        } else if (commentToDelete) {
            await handleDeleteComment(commentToDelete.task.id, commentToDelete.comment);
            setCommentToDelete(null);
        }
    };

    const handleDirectDeleteTask = async (task) => {
        if (!task) return;
        await handleDeleteTask(task.id);
        if (selectedTask?.id === task.id) setSelectedTask(null);
    };
    
    const handleUnarchiveTask = async (taskId) => {
        await handleUpdateTask(taskId, { isArchived: false, status: 'A Fazer' }, 'desarquivou a tarefa.');
    };

    const handleCreativeUnblock = useCallback(async (task) => {
        setSelectedTask(task);
        setIsCreativeModalOpen(true);
        setIsCreativeLoading(true);
        setCreativeSuggestion('');
        const prompt = `Aja como um assistente criativo. Tarefa: "${task.title}". Cliente: "${task.client}". Descrição: "${task.description}". Forneça um primeiro passo acionável e 2 pontos de atenção em português do Brasil. Formato: **Primeiro Passo:** [texto] **Pontos de Atenção:** * [texto] * [texto]`;
        try {
            const result = await callGeminiAPI(prompt);
            setCreativeSuggestion(result || "Não foi possível gerar uma sugestão.");
        } catch (error) {
            setCreativeSuggestion("Ocorreu um erro ao buscar a sugestão. Tente novamente.");
        } finally {
            setIsCreativeLoading(false);
        }
    }, []);

    const archivedTasksData = tasks.filter(task => task.isArchived);

    return (
        <div className="bg-gray-900 text-white min-h-screen font-sans">
            <MainLayout
                user={currentUserProfile}
                onLogout={handleLogout}
                onAddClick={handleOpenNewTaskModal}
                onSettingsClick={() => setShowSettingsModal(true)}
                onOpenPalette={() => setIsPaletteOpen(true)}
                onOpenAvatarModal={() => setIsAvatarModalOpen(true)}
                isSidebarCollapsed={isSidebarCollapsed}
                onToggleSidebar={() => setIsSidebarCollapsed(p => !p)}
                currentView={view}
                onViewChange={setView}
                onCloseDetails={closeTaskDetails}
            >
                {view === 'dashboard' && <DashboardPage onClientClick={handleClientClick} onTaskClick={openTaskDetails} />}
                {view === 'flow' && <FlowPage onTaskClick={openTaskDetails} onNewTaskClick={handleOpenNewTaskModal} />}
                {view === 'projects' && <ProjectsList onAddProject={() => { setProjectToEdit(null); setView('project-registration'); }} onProjectClick={handleProjectClick} onDeleteProject={(p) => setItemToDelete({ type: 'project', data: p })} />}
                {view === 'clients' && <ClientsList onAdd={() => { setClientToEdit(null); setView('client-registration'); }} onClientClick={handleClientClick} />}
                {view === 'quotes' && <QuoteGeneratorPage onDeleteQuote={(q) => setItemToDelete({ type: 'quote', data: q })} quoteIdToOpen={quoteIdToOpen} setQuoteIdToOpen={setQuoteIdToOpen} />}
                {view === 'tools' && <ToolsPage />}
                {view === 'client-registration' && <ClientRegistrationForm clientToEdit={clientToEdit} onSave={(data) => { handleSaveClient(data); setView('clients'); }} onCancel={() => setView('clients')} />}
                {view === 'project-registration' && <ProjectForm projectToEdit={projectToEdit} onSave={(data) => { handleSaveProject(data); setView('projects'); }} onCancel={() => setView('projects')} />}
                {view === 'client-details' && <ClientDetails client={selectedClient} onBack={() => setView('clients')} onSave={handleSaveClient} onDelete={(c) => setItemToDelete({ type: 'client', data: c })} onProjectClick={handleProjectClick} onQuoteClick={handleQuoteClick} />}
                {view === 'project-details' && <ProjectDetails project={selectedProject} onBack={() => setView('projects')} onEdit={(p) => { setProjectToEdit(p); setView('project-registration'); }} onDelete={(p) => setItemToDelete({ type: 'project', data: p })} onTaskClick={openTaskDetails} onNewTaskClick={handleOpenNewTaskModal} />}
                {view === 'archived' && <ArchivedTasks tasks={archivedTasksData} onUnarchive={handleUnarchiveTask} onTaskClick={openTaskDetails} onDeletePermanently={(task) => setItemToDelete({ type: 'task', data: task })} />}
            </MainLayout>

            {selectedTask && (
                 <div className="fixed inset-0 z-50 bg-gray-900/80 backdrop-blur-sm animate-fade-in">
                    <div ref={taskDetailsContainerRef} className="h-full overflow-y-auto">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                            <TaskDetails 
                                task={selectedTask} 
                                onDelete={() => handleDirectDeleteTask(selectedTask)} 
                                onBack={closeTaskDetails} 
                                onDeleteCommentRequest={(task, comment) => setCommentToDelete({ task, comment })}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* --- INÍCIO DA ALTERAÇÃO 2: Passando o novo handler para a paleta --- */}
            <CommandPalette isOpen={isPaletteOpen} onClose={() => setIsPaletteOpen(false)} onSelectCommand={handlePaletteSelection} />
            {/* --- FIM DA ALTERAÇÃO 2 --- */}
            <WelcomeModal isOpen={showFirstTimeWelcome} onClose={handleMarkWelcomeAsSeen} />
            <NewTaskModal isOpen={isNewTaskModalOpen} onClose={() => setIsNewTaskModalOpen(false)} onSave={handleSaveNewTask} initialStatus={newTaskDefaultStatus} clients={clients} projects={projects} initialProjectId={newTaskInitialProjectId} />
            <SettingsModal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)} />
            {selectedTask && <CreativeUnblockModal isOpen={isCreativeModalOpen} onClose={() => setIsCreativeModalOpen(false)} task={selectedTask} suggestion={creativeSuggestion} isLoading={isCreativeLoading} />}
            <ConfirmDeleteModal 
                item={itemToDelete.data || commentToDelete?.comment} 
                type={itemToDelete.type || (commentToDelete ? 'comment' : null)} 
                onConfirm={handleConfirmDelete} 
                onCancel={() => { setItemToDelete({ type: null, data: null }); setCommentToDelete(null); }} 
            />
            <UsernameSetupModal isOpen={isUsernameSetupRequired} onSave={handleUsernameSave} />
            <AvatarSelectionModal isOpen={isAvatarModalOpen} onClose={() => setIsAvatarModalOpen(false)} onSelect={handleSelectAvatar} />
        </div>
    );
};

const App = () => {
    const { user, isLoading } = useData();
    useEffect(() => {
        document.title = "V-Flow";
        let favicon = document.querySelector("link[rel~='icon']");
        if (!favicon) {
            favicon = document.createElement('link');
            favicon.rel = 'icon';
            document.head.appendChild(favicon);
        }
        favicon.href = '/assets/favicon.png';
    }, []);

    if (isLoading) {
        return <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 text-white text-xl">Carregando...</div>;
    }
    
    return (
        <>
            {user 
                ? <DndProvider backend={HTML5Backend}><VFlowApp /></DndProvider> 
                : <AuthPage />
            }
            <div id="portal-root"></div>
        </>
    );
};

export default App;